import { resourceFactory, resource, cell } from 'ember-resources';
import { compileMD, compileGJS, compileHBS } from './formats.js';
import { nameFor } from './utils.js';

const CACHE = new Map();
const SUPPORTED_FORMATS = ['glimdown', 'gjs', 'hbs'];

/**
 * Compile GitHub-flavored Markdown with GJS support
 * and optionally render gjs-snippets via a `live` meta tag
 * on the code fences.
 */

/**
 * Compile GJS
 */

/**
 * Compile a stateless component using just the template
 */

/**
 * This compileMD is a more robust version of the raw compiling used in "formats".
 * This function manages cache, and has events for folks building UIs to hook in to
 */
async function compile(text, options) {
  const {
    onSuccess,
    onError,
    onCompileStart
  } = options;
  const id = nameFor(`${options.format}:${text}`);
  const existing = CACHE.get(id);
  if (existing) {
    onSuccess(existing);
    return;
  }
  if (!SUPPORTED_FORMATS.includes(options.format)) {
    await onError(`Unsupported format: ${options.format}. Supported formats: ${SUPPORTED_FORMATS}`);
    return;
  }
  await onCompileStart();
  if (!text) {
    await onError('No Input Document yet');
    return;
  }
  let result;
  if (options.format === 'glimdown') {
    result = await compileMD(text, options);
  } else if (options.format === 'gjs') {
    result = await compileGJS(text, options.importMap);
  } else if (options.format === 'hbs') {
    result = await compileHBS(text, {
      scope: options.topLevelScope
    });
  } else {
    await onError(`Unsupported format: ${options.format}. Supported formats: ${SUPPORTED_FORMATS}`);
    return;
  }
  if (result.error) {
    await onError(result.error.message || `${result.error}`);
    return;
  }
  CACHE.set(id, result.component);
  await onSuccess(result.component);
}

/**
 * @internal
 */

/**
 * By default, this compiles to `glimdown`. A Markdown format which
 * extracts `live` tagged code snippets and compiles them to components.
 */
function Compiled(markdownText, maybeOptions) {
  return resource(() => {
    const maybeObject = typeof maybeOptions === 'function' ? maybeOptions() : maybeOptions;
    const format = (typeof maybeObject === 'string' ? maybeObject : maybeObject?.format) || 'glimdown';
    const options = (typeof maybeObject === 'string' ? {} : maybeObject) || {};
    const input = typeof markdownText === 'function' ? markdownText() : markdownText;
    const ready = cell(false);
    const error = cell();
    const result = cell();
    if (input) {
      compile(input, {
        // narrowing is hard here, but this is an implementation detail
        format: format,
        onSuccess: async component => {
          result.current = component;
          ready.set(true);
          error.set(null);
        },
        onError: async e => {
          error.set(e);
        },
        onCompileStart: async () => {
          ready.set(false);
        },
        ...options
      });
    }
    return () => ({
      isReady: ready.current,
      error: error.current,
      component: result.current
    });
  });
}
resourceFactory(Compiled);

export { CACHE, Compiled, compile };
//# sourceMappingURL=index.js.map
